/**
 * The space event ring (plan §2.6): a second preallocated SoA ring beside the M1 combat ring, written by the M2
 * sim stages and the career runtime and read by any number of independent cursors (career runtime, HUD,
 * audio, fx, views). Allocation-free (D-014). The M1 combat ring is not changed; M2 also emits M1 `hitHull`
 * events there for station impacts, bursts and coolant vents so the M1 HUD, camera shake and audio react.
 *
 * Reader pattern (as in src/combat/events.ts):
 * ```ts
 * let cursor = 0
 * // each frame or tick:
 * for (let i = firstReadableSpace(ring, cursor); i < ring.head; i++) {
 *   const s = i & SEV_MASK
 *   if (ring.type[s] === SEV.oreCollected) { ... ring.a[s] ... ring.f[s] ... }
 * }
 * cursor = ring.head
 * ```
 *
 * FROZEN after wave 1 (plan §1.2): new event types may be appended (never renumber).
 */

export const SPACE_EVENT_CAPACITY = 512
export const SEV_MASK = SPACE_EVENT_CAPACITY - 1

/**
 * Space event types. Field conventions (unused int fields are 0, `eid` is -1 when no ship is involved; x/y/z are
 * float64 world metres):
 * - `toolMode`: eid ship, a = the new `TOOL` mode.
 * - `navTarget`: eid ship, a = `NAV` kind, b = index (runtime index of that kind; `NAV.none` = cleared).
 * - `scanComplete`: eid ship, a = `NAV` kind, b = index (the tools runtime's scan readout holds the result).
 * - `chipBreak`: eid ship, a = rock index, b = `NAV` kind (rock or fragment), x/y/z chunk spawn point, f = tonnes.
 * - `oreCollected`: eid ship, a = ware index (`WARE_IDS`), f = tonnes, x/y/z capture point. The career adds it
 *   to the hold (and counts tetherstone from it).
 * - `holdFull`: eid ship, a = `HOLD_FULL_SOURCE` (0 chunk, 1 part). Fired once per rejection streak.
 * - `tetherstone`: eid ship, f = tonnes, x/y/z. VFX and audio only (the gate flicker); the career never counts it.
 * - `fractureStart` / `fractureSuccess` / `fractureBurst`: eid ship, a = rock index, b = fragments spawned
 *   (success) or chunks left (burst), c = `NAV` kind of the rock, f = damage dealt to the player (burst), x/y/z.
 * - `overheat` / `cooled`: eid ship.
 * - `anchorCut`: eid ship, a = part slot, b = anchor index, c = 1 for a coolant anchor, x/y/z anchor.
 * - `coolantVent`: eid ship, a = part slot, f = damage dealt to the player, x/y/z vent point.
 * - `partFreed`: eid ship, a = part slot, x/y/z.
 * - `partSecured`: eid ship, a = component type index (`COMPONENT_TYPES`), b = round(condition × 1000),
 *   c = slot in the salvage runtime's `securedLog`, f = value (credits), g = tonnes, x/y/z. The career builds the
 *   `CargoComponent` from `securedLog.entries[c]`.
 * - `partRejected`: eid ship, a = part slot, b = `REJECT_REASON`.
 * - `wreckSpawned` / `wreckLooted` / `wreckDespawned`: a = wreck slot, b = ship def index (-1 derelict section),
 *   c = `WRECK_KIND`, x/y/z.
 * - `clearanceGranted` / `clearanceDenied` / `clearanceExpired`: eid ship, a = station index, b = `CLEARANCE_REASON`
 *   (denied only), f = clearance seconds (granted). A moor grant (M10 P1e2) is a `clearanceGranted` with c = 1 and
 *   f = 0 (no clearance window), followed at once by `moorGranted`; a bay grant has c = 0.
 * - `autodockStart` / `autodockCancel`: eid ship, a = station index.
 * - `docked` / `undocked`: eid ship, a = station index, x/y/z.
 * - `impact`: eid ship, a = station index, f = hull damage, g = impact speed (m/s), x/y/z ship position.
 * - `playerDied`: eid = the KILLER (-1 unknown or none), a = the player's eid, x/y/z death position.
 * - `encounterSpawned` / `groupDefeated`: a = group id, b = `ENCOUNTER_KIND`, c = ship count, x/y/z group centre.
 *   A contract group's contract id comes from `EncountersRuntime.contractOf(a)`.
 * - `career`: a = `CAREER_NOTICE` code, f = amount (credits) where one applies. Emitted by the career runtime.
 * - `moorGranted` (M10 P1e2): eid ship, a = station index, b = ring slot. The moor autopilot is flying the ship to
 *   its mooring-ring point. The HUD's channel for the moor (moor state may not ride on `DockingReadout`).
 * - `moored` (M10 P1e2): eid ship, a = station index, b = ring slot, x/y/z. Emitted right after the `docked` of a
 *   moor completion: moored counts as docked.
 */
export const SEV = {
  toolMode: 1,
  navTarget: 2,
  scanComplete: 3,
  chipBreak: 4,
  oreCollected: 5,
  holdFull: 6,
  tetherstone: 7,
  fractureStart: 8,
  fractureSuccess: 9,
  fractureBurst: 10,
  overheat: 11,
  cooled: 12,
  anchorCut: 13,
  coolantVent: 14,
  partFreed: 15,
  partSecured: 16,
  partRejected: 17,
  wreckSpawned: 18,
  wreckLooted: 19,
  wreckDespawned: 20,
  clearanceGranted: 21,
  clearanceDenied: 22,
  clearanceExpired: 23,
  autodockStart: 24,
  autodockCancel: 25,
  docked: 26,
  undocked: 27,
  impact: 28,
  playerDied: 29,
  encounterSpawned: 30,
  groupDefeated: 31,
  career: 32,
  moorGranted: 33,
  moored: 34,
} as const
export type SpaceEventType = (typeof SEV)[keyof typeof SEV]

/** `SEV.career` notice codes (field `a`). */
export const CAREER_NOTICE = {
  leasePaid: 1,
  paymentMissed: 2,
  collections: 3,
  repoWarning: 4,
  ownership: 5,
  contractCompleted: 6,
  contractFailed: 7,
  deathFee: 8,
  saved: 9,
  saveFailed: 10,
} as const
export type CareerNoticeCode = (typeof CAREER_NOTICE)[keyof typeof CAREER_NOTICE]

/** `SEV.holdFull` field `a`. */
export const HOLD_FULL_SOURCE = { chunk: 0, part: 1 } as const

/** `SEV.partRejected` field `b`. */
export const REJECT_REASON = { tooLarge: 0, holdFull: 1, claimed: 2 } as const

/**
 * `SEV.clearanceDenied` field `b`. `tooLarge` (M10 P1e1): the player ship's size band is one the station's bay
 * doesn't take (`bands.ts bayAccepts`), so it moors instead ("Moors only: can't enter station bays").
 */
export const CLEARANCE_REASON = { none: 0, tooFar: 1, notReady: 2, hostileLock: 3, tooLarge: 4 } as const

/** `SEV.encounterSpawned` / `SEV.groupDefeated` field `b`. */
export const ENCOUNTER_KIND = { raiders: 0, repo: 1, contract: 2 } as const
export type EncounterKind = (typeof ENCOUNTER_KIND)[keyof typeof ENCOUNTER_KIND]

export interface SpaceEventRing {
  type: Uint8Array
  eid: Int32Array
  a: Int32Array
  b: Int32Array
  c: Int32Array
  x: Float64Array
  y: Float64Array
  z: Float64Array
  f: Float32Array
  g: Float32Array
  /** Total events ever emitted. Event `i` lives in slot `i & SEV_MASK` while `i >= head - SPACE_EVENT_CAPACITY`. */
  head: number
}

export function createSpaceEventRing(): SpaceEventRing {
  return {
    type: new Uint8Array(SPACE_EVENT_CAPACITY),
    eid: new Int32Array(SPACE_EVENT_CAPACITY),
    a: new Int32Array(SPACE_EVENT_CAPACITY),
    b: new Int32Array(SPACE_EVENT_CAPACITY),
    c: new Int32Array(SPACE_EVENT_CAPACITY),
    x: new Float64Array(SPACE_EVENT_CAPACITY),
    y: new Float64Array(SPACE_EVENT_CAPACITY),
    z: new Float64Array(SPACE_EVENT_CAPACITY),
    f: new Float32Array(SPACE_EVENT_CAPACITY),
    g: new Float32Array(SPACE_EVENT_CAPACITY),
    head: 0,
  }
}

/** Appends an event, overwriting the oldest when full. Returns its sequence number. Allocation-free. */
export function emitSpaceEvent(
  ring: SpaceEventRing,
  type: SpaceEventType,
  eid: number,
  a: number,
  b: number,
  c: number,
  x: number,
  y: number,
  z: number,
  f: number,
  g: number,
): number {
  const seq = ring.head
  const s = seq & SEV_MASK
  ring.type[s] = type
  ring.eid[s] = eid
  ring.a[s] = a
  ring.b[s] = b
  ring.c[s] = c
  ring.x[s] = x
  ring.y[s] = y
  ring.z[s] = z
  ring.f[s] = f
  ring.g[s] = g
  ring.head = seq + 1
  return seq
}

/** The first sequence number a reader at `cursor` can still read (a reader more than a ring behind skips to the oldest). */
export function firstReadableSpace(ring: SpaceEventRing, cursor: number): number {
  const oldest = ring.head - SPACE_EVENT_CAPACITY
  return cursor > oldest ? cursor : oldest > 0 ? oldest : 0
}

/** Ring slot of sequence number `i`. */
export function spaceEventSlot(i: number): number {
  return i & SEV_MASK
}
